import { ThemeProvider } from "@/components/layout/dashboard/theme/context"
import { Button } from "@/components/ui/button"
import { SrOnly } from "@/components/ui/sr-only"
import { Tooltip } from "@/components/ui/tooltip"

import { Menu } from "lucide-react"
import { BaseLayoutProvider } from "../context"
import { Breadcrumb, Breadcrumbs } from "./breadcrumbs"
import { Sidebar } from "./sidebar"
import { SidebarContext, SidebarProvider } from "./sidebar/context"
import { UserDropdown } from "./user-dropdown"

/**
 * Layout
 */
type LayoutProps = {
  children: React.ReactNode
}
const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <BaseLayoutProvider isDashboard>
      <ThemeProvider>
        <SidebarProvider>
          <div
            className="fixed inset-0 -z-10 dark:bg-black bg-white dark:bg-grid-white/[0.1] bg-grid-black/[0.03] size-full pointer-events-none"
            aria-hidden
          >
            <div className="absolute inset-0 size-full dark:bg-black bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
          </div>
          <div className="relative">
            <Sidebar />
            {children}
          </div>
        </SidebarProvider>
      </ThemeProvider>
    </BaseLayoutProvider>
  )
}
export default Layout

/**
 * PageWrapper
 */
export const PageWrapper = React.forwardRef<HTMLDivElement, React.ComponentProps<"main">>(
  ({ className, ...props }, ref) => {
    const sidebar = React.useContext(SidebarContext)
    return (
      <main
        ref={ref}
        className={cx(
          "md:transition-all md:ease-in-out md:duration-300 pr-4 pt-4 pl-4 pb-4",
          sidebar.state ? "md:ml-[320px]" : "md:ml-24",
          className
        )}
        {...props}
      />
    )
  }
)

/**
 * PageContent
 */
export const PageContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cx(className)} {...props} />
  }
)

/**
 * TempToggle
 */
export const SidebarToggle: React.FC = () => {
  const { _ } = useDictionary("components.layout.sidebar")
  const sidebar = React.useContext(SidebarContext)
  return (
    <Tooltip side="left" content={_("button-toggle")}>
      <Button onClick={sidebar.toggle} variant="secondary" icon className="relative">
        <Menu size={16} aria-hidden />
        <SrOnly>{_("button-toggle")}</SrOnly>
      </Button>
    </Tooltip>
  )
}

/**
 * PageHeader
 */
type PageHeaderProps = {
  breadcrumbs: Breadcrumb[]
  syntheticBreadcrumbs?: React.ReactNode
  image?: string
  children?: React.ReactNode
}
export const PageHeader: React.FC<PageHeaderProps> = ({ breadcrumbs, image, children }) => {
  return (
    <>
      {G.isNotNullable(image) && (
        <div
          className={cx(
            "absolute top-0 inset-x-0 -z-50 h-24 bg-cover bg-no-repeat bg-center opacity-1"
          )}
          style={{ backgroundImage: `url('${image}')` }}
        />
      )}
      <div className="flex justify-between items-center pb-4 gap-2">
        <SidebarToggle />
        <Breadcrumbs {...{ breadcrumbs }} className="grow" />
        <div className="flex gap-2">
          {G.isNotNullable(children) && <div>{children}</div>}
          <UserDropdown align="end" side="bottom" />
        </div>
      </div>
    </>
  )
}
